/**
 * Testi dell'aiuto in linea (QA26-F5.2) nel formato di @brandart/help-kit: i generici del kit (IT dal kit, EN qui), le
 * schede della presentazione e i suggerimenti da @qa/shared (onboarding.ts), i testi del pannello dell'Assistente.
 * Indipendenti dalle risorse i18next della shell: la lingua si passa.
 */
import { createTranslator, DEFAULT_TEXTS_IT, mergeTexts, type HelpTranslate, type TextTree } from "@brandart/help-kit";
import { PRESENTAZIONE, RUOLI, SUGGERIMENTI, type Lingua, type Ruolo } from "@qa/shared";

/** I testi generici del kit in inglese (stesse chiavi di DEFAULT_TEXTS_IT), come Governance. */
export const KIT_EN: TextTree = {
  guide: { title: "Guide", downloadPdf: "Download PDF", close: "Close" },
  onboarding: {
    dialogLabel: "Introduction",
    close: "Close the introduction",
    progress: "{{n}} of {{total}}",
    dotsLabel: "Introduction slides",
    goTo: "Go to slide {{n}}",
    skip: "Skip",
    back: "Back",
    next: "Next",
    start: "Start",
    guideHint: "Skipped the introduction or want to see it again?",
    replay: "Replay the introduction",
  },
  tips: {
    kicker: "Tip",
    gotIt: "Got it",
    askAssistant: "Ask the assistant",
    disableAll: "Don't show tips again",
    announce: "Tip: {{title}}",
    reenable: "Turn tips back on",
    reenabled: "Tips turned back on.",
  },
};

/** Id della scheda nel dizionario (senza punti: il punto separa le chiavi). */
export const idScheda = (ruolo: Ruolo, i: number) => `${ruolo}_${i + 1}`;

/** Scrive `valore` nel dizionario al percorso `a.b.c` (le ancore possono avere il punto: `tips.nc.owner.title`). */
function metti(tree: TextTree, percorso: string, valore: string) {
  const parti = percorso.split(".");
  let n = tree;
  for (const p of parti.slice(0, -1)) n = (n[p] ??= {}) as TextTree;
  n[parti.at(-1)!] = valore;
}

/** Presentazione e suggerimenti nella lingua data. */
export function testiAiuto(lingua: Lingua): TextTree {
  const t: TextTree = {};
  for (const r of RUOLI)
    PRESENTAZIONE[r].forEach((s, i) => {
      metti(t, `onboarding.slides.${idScheda(r, i)}.title`, s.titolo[lingua]);
      metti(t, `onboarding.slides.${idScheda(r, i)}.body`, s.testo[lingua]);
    });
  for (const s of SUGGERIMENTI) {
    metti(t, `tips.${s.anchor}.title`, s.titolo[lingua]);
    metti(t, `tips.${s.anchor}.text`, s.testo[lingua]);
    if (s.domanda) metti(t, `tips.${s.anchor}.ask`, s.domanda[lingua]);
  }
  return t;
}

const TRADUTTORI: Record<Lingua, HelpTranslate> = {
  it: createTranslator(mergeTexts(DEFAULT_TEXTS_IT, testiAiuto("it"))),
  en: createTranslator(mergeTexts(KIT_EN, testiAiuto("en"))),
};
/** Il `t` del kit per la lingua. */
export const tKit = (lingua: Lingua): HelpTranslate => TRADUTTORI[lingua];

/** Testi della testata e del pannello dell'Assistente. */
export const UI = {
  it: {
    guida: "Guida",
    assistente: "Assistente",
    titolo: "Assistente Qualità",
    sottotitolo: "assistente AI · vede solo quello che vedi tu, non modifica niente",
    nuova: "Nuova conversazione",
    intro: "Chiedi sui red flag, le NC, i claim e su come si usa il portale.",
    perEsempio: "Per esempio:",
    esempi: ["Quali NC critiche non hanno ancora un owner?", "Quali azioni sono scadute?", "Com'è andata la settimana per i red flag?"],
    placeholder: "Chiedi sui red flag, le NC, i claim…",
    invia: "Invia",
    errore: "L'assistente non risponde: riprova tra poco",
  },
  en: {
    guida: "Guide",
    assistente: "Assistant",
    titolo: "Quality Assistant",
    sottotitolo: "AI assistant · sees only what you see, changes nothing",
    nuova: "New conversation",
    intro: "Ask about red flags, NCs, claims and how to use the portal.",
    perEsempio: "For example:",
    esempi: ["Which critical NCs have no owner yet?", "Which actions are overdue?", "How did the week go for red flags?"],
    placeholder: "Ask about red flags, NCs, claims…",
    invia: "Send",
    errore: "The assistant is not responding: try again shortly",
  },
} satisfies Record<Lingua, Record<string, string | readonly string[]>>;
